import React, { useState, useEffect} from "react";
import ReactSelect from "react-select";
import { 
  getScrapTyreKmReport, 
  getScrapTyreKmHubReport,
  getCustomerFleets,
  getScrapTyreKmReportTyreData,
  getScrapTyreKmHubReportTyreData,
  downloadScrapTyreKmReportTyreData,
  downloadScrapTyreKmHubReportTyreData
} from "../../apis/scrappedCasingTyres.service";
import Spinner from "../Spinner";
import {
  MONTH_OPTIONS,
  kmOptions,
  SCRAP_TYRE_KM_REPORT_DETAIL_COLUMNS,
} from "../../utils/scrapConstants";
import CustomDetailModal from "./CustomDetailModal";
import { BiCloudDownload } from "react-icons/bi";
import toast from "react-hot-toast";
import { exportScrapCasingTyresExcel } from "../../utils/exportExcel";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

function getMonthsInDateRange(dateFromStr, dateToStr) {
    if (!dateFromStr || !dateToStr) return MONTH_OPTIONS;
    const start = new Date(`${dateFromStr}T12:00:00`);
    const end = new Date(`${dateToStr}T12:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];
    const seen = new Set();
    let y = start.getFullYear();
    let m = start.getMonth();
    const endY = end.getFullYear();
    const endM = end.getMonth();
    while (y < endY || (y === endY && m <= endM)) {
        seen.add(m + 1);
        if (seen.size === 12) break;
        m += 1;
        if (m > 11) {
            m = 0;
            y += 1;
        }
    }
    return MONTH_OPTIONS.filter((opt) => seen.has(opt.value));
}

const styles = {
    container: (noCard) => `w-full ${noCard ? "p-0" : "max-w-[1400px]"}`,
    cardHeader: "w-full bg-white mb-6 p-[16px_24px] border-[1.5px] border-[#C6C6C6] rounded-[6px]",
    headerContent: "flex flex-row flex-nowrap justify-between items-center gap-3",
    title: "font-inter font-semibold text-[18px] leading-none text-[#4841B9] bg-transparent whitespace-nowrap",
    filterWrapper: "flex items-center gap-2 whitespace-nowrap shrink-0",
    selectWrapper: "w-[150px]",
    filterRow: "flex items-center gap-2",
    filterLabel: "text-[#2C2C2C] text-xs font-medium mr-1",
    select: "bg-transparent border-none appearance-none outline-none cursor-pointer font-inherit text-inherit text-[#2C2C2C]",
    contentCard: (noCard) => `w-full ${noCard ? "bg-transparent" : "bg-white p-6 rounded-3xl shadow-[0_4px_20px_rgba(0,0,0,0.05)]"}`,
    breadcrumbWrapper: "flex items-center gap-2 mb-6 min-h-[24px]",
    breadcrumbLink: "font-inter text-[14px] leading-[20px] text-[#4841B9] italic font-normal text-decoration-none cursor-pointer transition-colors hover:opacity-80",
    breadcrumbSeparator: "text-[#4841B9] font-medium",
    breadcrumbCurrent: "font-inter text-[14px] leading-[20px] text-[#4841B9] italic font-semibold text-decoration-none",
    tableWrapper: "w-full overflow-x-auto overflow-y-auto min-h-[250px] max-h-[520px] border border-[#C6C6C6] rounded-[6px] bg-white",
    table: "w-full text-left border-collapse min-w-max",
    thead: "sticky top-0 bg-white z-10",
    thRow: "border-b border-[#C6C6C6] border-b-[1px]",
    th: "py-4 px-6 text-[15px] font-bold text-gray-800 whitespace-nowrap text-left ",
    thCenter: "py-5 px-6 text-[15px] font-bold text-gray-800 text-center whitespace-nowrap border-none",
    tbody: "text-[#363636] text-[14px]",
    tr: (viewMode) => `border-b border-[#C6C6C6] border-b-[0.5px] transition-colors ${viewMode === "fleets" ? "cursor-pointer hover:bg-gray-50/50" : ""}`,
    td: "py-5 px-6 font-medium text-gray-700 whitespace-nowrap text-left",
    tdCenter: "py-5 px-6 text-gray-500 text-center whitespace-nowrap",
    tdBoldCenter: "py-5 px-6 text-gray-700 font-bold text-center whitespace-nowrap border-none",
    totalRow: "bg-gray-50/10 font-bold",
    totalTd: "py-5 px-6 text-gray-800 whitespace-nowrap text-left uppercase tracking-wider",
    totalTdCenter: "py-5 px-6 text-gray-700 text-center whitespace-nowrap",
    totalTdBoldCenter: "py-5 px-6 text-gray-800 text-center whitespace-nowrap border-none",
    emptyRow: "py-16 text-center text-gray-500 font-medium italic"
};

const ScrapTyreKmReportTable = ({ noCard = false }) => {
    const [viewMode, setViewMode] = useState("fleets"); 
    const [selectedFleet, setSelectedFleet] = useState(null); 

    const [kmRange, setKmRange] = useState("25000");
    const [selectedDetail, setSelectedDetail] = useState(null);
    const defaultDateFrom = new Date();
    defaultDateFrom.setMonth(defaultDateFrom.getMonth() - 3);
    const todayDate = dayjs();
    const [dateFrom, setDateFrom] = useState(defaultDateFrom.toISOString().split("T")[0]);
    const [dateTo, setDateTo] = useState(new Date().toISOString().split("T")[0]);
    const [fleetList, setFleetList] = useState([]);
    const [fleetFilter, setFleetFilter] = useState({ value: "all", label: "Select Fleet" });
    const [allRows, setAllRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalData, setModalData] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);

    const handleDateFromChange = (value) => {
        if (!value) {
            setDateFrom("");
            return;
        }
        if (!value.isValid()) return;
        if (value.isAfter(todayDate, "day")) return;
        if (dateTo && value.isAfter(dayjs(dateTo), "day")) return;
        setDateFrom(value.format("YYYY-MM-DD"));
    };

    const handleDateToChange = (value) => {
        if (!value) {
            setDateTo("");
            return;
        }
        if (!value.isValid()) return;
        if (value.isAfter(todayDate, "day")) return;
        if (dateFrom && value.isBefore(dayjs(dateFrom), "day")) return;
        setDateTo(value.format("YYYY-MM-DD"));
    };

    

    useEffect(() => {
        const fetchFleets = async () => {
          try {
            const response = await getCustomerFleets();
            if (response?.data) {
              const options = response.data.map(f => ({ value: f._id, label: f.fleetName }));
              setFleetList([{ value: "all", label: "Select Fleet" }, ...options]);
            }
          } catch (error) {
            console.error("Error fetching fleets:", error);
          }
        };
        fetchFleets();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            let response;
            const payload = {
                kmRange: Number(kmRange),
                dateFrom,
                dateTo,
            };

            if (viewMode === "fleets") {
                if (fleetFilter?.value !== "all") payload.fleetId = fleetFilter?.value;
                response = await getScrapTyreKmReport(payload);
            } else {
                response = await getScrapTyreKmHubReport(selectedFleet.id, payload);
            }

            if (response?.data && Array.isArray(response.data)) {
                setAllRows(response.data);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [kmRange, dateFrom, dateTo, fleetFilter, viewMode, selectedFleet]);

    const activeMonths = getMonthsInDateRange(dateFrom, dateTo);

    const monthKeys = activeMonths.map(m => m.label.toLowerCase());

    /** Same body as modal fetch + download APIs — keep in sync. */
    const buildScrapTyreKmModalApiPayload = () => {
        if (!selectedDetail) return null;
        const payload = {
            kmRange: Number(kmRange),
            dateFrom,
            dateTo,
            month: selectedDetail.month,
        };
        if (viewMode === "fleets") {
            if (selectedDetail.resourceId) {
                payload.fleetId = selectedDetail.resourceId;
            }
        } else {
            if (!selectedFleet?.id) return null;
            payload.fleetId = selectedFleet.id;
            if (selectedDetail.resourceId) {
                payload.hubId = selectedDetail.resourceId;
            }
        }
        return payload;
    };

    useEffect(() => {
        if (selectedDetail) {
            const fetchModalData = async () => {
                setModalLoading(true);
                try {
                    const payload = buildScrapTyreKmModalApiPayload();
                    if (!payload) {
                        setModalData([]);
                        return;
                    }

                    const response =
                        viewMode === "fleets"
                            ? await getScrapTyreKmReportTyreData(payload)
                            : await getScrapTyreKmHubReportTyreData(payload);

                    if (response?.success) {
                        setModalData(response.data || []);
                    }
                } catch (error) {
                    console.error("Error fetching modal data:", error);
                } finally {
                    setModalLoading(false);
                }
            };
            fetchModalData();
        } else {
            setModalData([]);
        }
    }, [selectedDetail, viewMode, kmRange, dateFrom, dateTo, selectedFleet]);

    const resolveDownloadTyreRows = (response) => {
        if (!response) return [];
        if (Array.isArray(response)) return response;
        if (Array.isArray(response.data)) return response.data;
        return [];
    };

    const handleDownloadModalData = async () => {
        if (!selectedDetail) return;
        const payload = buildScrapTyreKmModalApiPayload();
        if (!payload) {
            toast.error(
                viewMode === "hubs"
                    ? "Fleet context is missing for download"
                    : "Could not build download request"
            );
            return;
        }
        setDownloadLoading(true);
        try {
            const response =
                viewMode === "fleets"
                    ? await downloadScrapTyreKmReportTyreData(payload)
                    : await downloadScrapTyreKmHubReportTyreData(payload);
            const list = resolveDownloadTyreRows(response);
            if (!list.length) {
                toast.error("No data to download");
                return;
            }
            const safeTitle = `Scrapped-Tyres-${selectedDetail.resource}-${selectedDetail.type}`
                .replace(/\s+/g, "_")
                .replace(/[/\\?%*:|"<>]/g, "-");
            exportScrapCasingTyresExcel(list, `scrap-tyre-km-report_${safeTitle}`, { tyreStatus: 'scrap' });
            toast.success("File downloaded");
        } catch (err) {
            console.error("KM report download failed:", err);
        } finally {
            setDownloadLoading(false);
        }
    };

    const totals = allRows.reduce((acc, row) => {
        monthKeys.forEach(key => {
            acc[key] = (acc[key] || 0) + (row[key] || 0);
        });
        acc.grandTotal = (acc.grandTotal || 0) + (row.totalCount || 0);
        return acc;
    }, {});

    const filterInputS = {
        height: "36px",
        border: "1px solid #858585",
        borderRadius: "6px",
        padding: "0 6px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        backgroundColor: "white",
        fontSize: "14px",
        fontWeight: "400",
        fontFamily: "'Inter', sans-serif",
        color: "#2C2C2C",
        cursor: "pointer",
        outline: "none"
    };

    const reactSelectCustomStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: "36px",
            height: "36px",
            minWidth: "140px",
            borderRadius: "6px",
            borderColor: "#858585",
            fontSize: "14px",
            fontWeight: "400",
            fontFamily: "'Inter', sans-serif",
            "&:hover": {
                borderColor: "#6B7280"
            },
            boxShadow: "none"
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: "0 8px",
            height: "36px",
            display: "flex",
            alignItems: "center"
        }),
        singleValue: (provided) => ({
            ...provided,
            color: "#2C2C2C"
        }),
        input: (provided) => ({
            ...provided,
            margin: "0px",
        }),
        indicatorSeparator: () => ({
            display: "none"
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: "36px",
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? "#4841B9" : state.isFocused ? "#F3F4F6" : "white",
            color: state.isSelected ? "white" : "#374151",
            fontSize: "14px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
        }),
        menu: (provided) => ({
            ...provided,
            width: "170px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            zIndex: 99
        }),
        menuList: (provided) => ({
            ...provided,
            maxHeight: "250px", 
            overflowX: "hidden",
            overflowY: "auto",
        })
    };

    return (
        <div className={styles.container(noCard)}>
            <div className={styles.cardHeader}>
                <div className={styles.headerContent}>
                    <h2 className={styles.title}>
                        {Number(kmRange) > 100000
                            ? "Scrapped Tyres Greater than 1 Lakh KM"
                            : `Scrapped Tyres within ${Number(kmRange).toLocaleString()} KM`}
                    </h2>

                    <div className={styles.filterWrapper}>
                        {viewMode === "fleets" && (
                             <div className={styles.selectWrapper}>
                                <ReactSelect
                                    options={fleetList}
                                    value={fleetFilter}
                                    onChange={(option) => {
                                        setFleetFilter(option);
                                    }}
                                    placeholder="Select Fleet"
                                    styles={reactSelectCustomStyles}
                                    isSearchable
                                />
                             </div>
                        )}

                        <select 
                            value={kmRange} 
                            onChange={(e) => {
                                setKmRange(e.target.value);
                            }} 
                            style={filterInputS}
                        >
                            {kmOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>

                        <div className={styles.filterRow}>
                            <div style={filterInputS}>
                                <span className={styles.filterLabel}>From:</span>
                                <DatePicker
                                    value={dateFrom ? dayjs(dateFrom) : null}
                                    onChange={handleDateFromChange}
                                    format="DD/MM/YYYY"
                                    maxDate={dateTo ? dayjs(dateTo) : todayDate}
                                    disableFuture
                                    slotProps={{
                                        actionBar: { actions: ["clear", "today"] },
                                        textField: {
                                            variant: "standard",
                                            sx: {
                                                width: 100,
                                                "& input": { fontSize: 12, padding: "9px 0" },
                                            },
                                            InputProps: { disableUnderline: true },
                                        },
                                    }}
                                />
                            </div>
                            <div style={filterInputS}>
                                <span className={styles.filterLabel}>To:</span>
                                <DatePicker
                                    value={dateTo ? dayjs(dateTo) : null}
                                    onChange={handleDateToChange}
                                    format="DD/MM/YYYY"
                                    minDate={dateFrom ? dayjs(dateFrom) : undefined}
                                    maxDate={todayDate}
                                    disableFuture
                                    slotProps={{
                                        actionBar: { actions: ["clear", "today"] },
                                        textField: {
                                            variant: "standard",
                                            sx: {
                                                width: 100,
                                                "& input": { fontSize: 12, padding: "9px 0" },
                                            },
                                            InputProps: { disableUnderline: true },
                                        },
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.contentCard(noCard)}>
                <div className={styles.breadcrumbWrapper}>
                    {viewMode === "hubs" && (
                        <React.Fragment>
                            <span
                                className={styles.breadcrumbLink}
                                onClick={() => {
                                    setViewMode("fleets");
                                    setSelectedFleet(null);
                                }}
                            >
                                Fleets
                            </span>
                            <span className={styles.breadcrumbSeparator}>&gt;</span>
                            <span className={styles.breadcrumbCurrent}>
                                {selectedFleet?.name} Hubs
                            </span>
                        </React.Fragment>
                    )}
                </div>
                <div className={styles.tableWrapper}>
                    {loading ? (
                        <div className="flex justify-center items-center h-[250px]">
                            <Spinner />
                        </div>
                    ) : (
                        <table className={styles.table}>
                            <thead>
                                <tr className={`${styles.thead} ${styles.thRow}`}>
                                    <th className={styles.th}>
                                        {viewMode === "fleets" ? "Fleet Name" : "Hub Name"}
                                    </th>
                                    {activeMonths.map(month => (
                                        <th key={month.value} className={styles.thCenter}>
                                            {month.label}
                                        </th>
                                    ))}
                                    <th className={styles.thCenter}>
                                        Grand Total
                                    </th>
                                </tr>
                            </thead>
                            <tbody className={styles.tbody}>
                                {allRows.length > 0 ? (
                                    <>
                                                {allRows.map((row, idx) => (
                                                    <tr 
                                                        key={idx} 
                                                        className={styles.tr(viewMode)}
                                                    >
                                                        <td 
                                                            className={`${styles.td} text-blue-600 hover:underline`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (viewMode === "fleets") {
                                                                    const fid = row.FleetId || row.fleetId || row._id;
                                                                    setSelectedFleet({ id: fid, name: row.fleetName });
                                                                    setViewMode("hubs");
                                                                }
                                                            }}
                                                        >
                                                            {viewMode === "fleets" ? (row.fleetName || 'Unknown Fleet') : (row.hubName || 'Unknown Hub')}
                                                        </td>
                                                        {monthKeys.map((key, mIdx) => (
                                                            <td 
                                                                key={key} 
                                                                className={`${styles.tdCenter} ${row[key] > 0 ? "text-blue-600 cursor-pointer hover:underline" : ""}`}
                                                                onClick={() => {
                                                                    if (row[key] > 0) {
                                                                        setSelectedDetail({
                                                                            resource: viewMode === "fleets" ? row.fleetName : row.hubName,
                                                                            resourceId: viewMode === "fleets" ? (row.FleetId || row.fleetId || row._id) : (row.hubId || row._id),
                                                                            type: activeMonths[mIdx].label,
                                                                            month: activeMonths[mIdx].value,
                                                                            value: row[key]
                                                                        });
                                                                    }
                                                                }}
                                                            >
                                                                {row[key] || 0}
                                                            </td>
                                                        ))}
                                                        <td 
                                                            className={`${styles.tdBoldCenter} ${row.totalCount > 0 ? "text-blue-600 cursor-pointer hover:underline" : ""}`}
                                                            onClick={() => {
                                                                if (row.totalCount > 0) {
                                                                    setSelectedDetail({
                                                                        resource: viewMode === "fleets" ? row.fleetName : row.hubName,
                                                                        resourceId: viewMode === "fleets" ? (row.FleetId || row.fleetId || row._id) : (row.hubId || row._id),
                                                                        type: "Grand Total",
                                                                        month: "all",
                                                                        value: row.totalCount
                                                                    });
                                                                }
                                                            }}
                                                        >
                                                            {row.totalCount || 0}
                                                        </td>
                                                    </tr>
                                                ))}
                                        <tr className={styles.totalRow}>
                                            <td className={styles.totalTd}>Grand Total</td>
                                            {monthKeys.map((key, mIdx) => (
                                                <td 
                                                    key={key} 
                                                    className={`${styles.totalTdCenter} ${totals[key] > 0 ? "text-blue-600 cursor-pointer hover:underline" : ""}`}
                                                    onClick={() => {
                                                        if (totals[key] > 0) {
                                                            setSelectedDetail({
                                                                resource: "All " + (viewMode === "fleets" ? "Fleets" : "Hubs"),
                                                                resourceId:
                                                                    viewMode === "fleets" && fleetFilter?.value !== "all"
                                                                        ? fleetFilter?.value
                                                                        : undefined,
                                                                type: activeMonths[mIdx].label,
                                                                month: activeMonths[mIdx].value,
                                                                value: totals[key]
                                                            });
                                                        }
                                                    }}
                                                >
                                                    {totals[key] || 0}
                                                </td>
                                            ))}
                                            <td 
                                                className={`${styles.totalTdBoldCenter} ${totals.grandTotal > 0 ? "text-blue-600 cursor-pointer hover:underline" : ""}`}
                                                onClick={() => {
                                                    if (totals.grandTotal > 0) {
                                                        setSelectedDetail({
                                                            resource: "All " + (viewMode === "fleets" ? "Fleets" : "Hubs"),
                                                            resourceId:
                                                                viewMode === "fleets" && fleetFilter?.value !== "all"
                                                                    ? fleetFilter?.value
                                                                    : undefined,
                                                            type: "Grand Total",
                                                            month: "all",
                                                            value: totals.grandTotal
                                                        });
                                                    }
                                                }}
                                            >
                                                {totals.grandTotal || 0}
                                            </td>
                                        </tr>
                                    </>
                                ) : (
                                    <tr>
                                        <td colSpan={activeMonths.length + 2} className={styles.emptyRow}>
                                            No data available for the selected filters
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
            
            <CustomDetailModal 
                modalWidth="85vw"
                isOpen={!!selectedDetail} 
                onClose={() => setSelectedDetail(null)} 
                title={`Scrapped Tyres - ${selectedDetail?.resource} (${selectedDetail?.type})`}
                loading={modalLoading}
                filterComponent={
                    <button
                        type="button"
                        className="flex items-center gap-2 bg-[#22C55E] text-white px-5 py-2.5 rounded-xl hover:bg-green-600 transition-all shadow-sm hover:shadow-md font-semibold text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                        disabled={downloadLoading || modalLoading || !selectedDetail}
                        onClick={handleDownloadModalData}
                    >
                        <BiCloudDownload size={20} className={downloadLoading ? "animate-pulse" : ""} />
                        <span>{downloadLoading ? "Preparing…" : "Download Data"}</span>
                    </button>
                }
                columns={SCRAP_TYRE_KM_REPORT_DETAIL_COLUMNS}
                data={modalData.map((d, idx) => ({
                    id: idx,
                    zone: d.zone || "-",
                    region: d.region || "-",
                    hubName: d.hubName || "-",
                    stencilNo: d.stencilNo || "-",
                    lastInspDate: d.lastInspectionDate ? new Date(d.lastInspectionDate).toLocaleDateString('en-GB') : "-",
                    tubeType: d.productType || "-",
                    scrapReason: d.scrapReason || "-",
                    validationDate: d.tyreValidationDate ? new Date(d.tyreValidationDate).toLocaleDateString('en-GB') : "-",
                    validatedBy: d.validatedBy || "-",
                }))}
            />
        </div>
    );
};

export default ScrapTyreKmReportTable;
